import { inject, Injectable, NgZone } from '@angular/core';
import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js';
import HoverPlugin from 'wavesurfer.js/dist/plugins/hover.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom.esm.js';

import { RecordData } from '../../../types';
import { RecordingEvent } from './measured-data-details.types';
import { MeasuredDataDetailsRegionService } from './measured-data-details-region.service';
import { WaveTimelineService } from './services/wave-timeline.service';
import {
  computeWavPeaksFromBuffer,
  streamWavPeaks,
  WavPeaksResult,
} from './services/wav-peak-extractor';

export interface WavePlayerCallbacks {
  onReady: () => void;
  onPlay: () => void;
  onPause: () => void;
  onFinish: () => void;
  onAudioProcess: () => void;
  onSeeking: (time: number) => void;
  onError: (error: any) => void;
  onLoading?: (percent: number) => void;
  onDownloadStart?: () => void;
  onDownloadProgress?: (percent: number | null) => void;
  onDownloadDone?: () => void;
  onProcessingStart?: () => void;
  onProcessingDone?: () => void;
}

export interface WavePlayerInitOptions {
  container: HTMLElement | null | undefined;
  recordData: RecordData | null | undefined;
  audioUrl?: string;
  videoDurationSec?: number | null;
  colors: {
    waveColor: string;
    progressColor: string;
    cursorColor: string;
  };
  callbacks: WavePlayerCallbacks;
  events: RecordingEvent[];
  regionService: MeasuredDataDetailsRegionService;
  waveTimeline: WaveTimelineService;
  hiddenRegionParams: any;
  timelineOffsetReference: number;
  currentStartOffset: number;
  wavDurationMs?: number | null;
  suppressReadyCallback?: boolean;
}

/**
 * WaveSurfer 本体と周辺プラグインの生成・破棄を担当するサービス。
 */
@Injectable({ providedIn: 'root' })
export class MeasuredDataDetailsWaveService {
  private readonly PEAKS_PER_SECOND_FOR_ZOOM = 800;
  private readonly MIN_RENDER_PEAKS = 16_000;
  private readonly MAX_RENDER_PEAKS = 480_000;
  private wavesurfer?: WaveSurfer;
  private hoverPlugin?: any;
  private regionsPlugin?: any;
  private timelineService?: WaveTimelineService;
  private lastInitOptions?: WavePlayerInitOptions;
  private initialMinPxPerSec?: number;
  private isReady = false;
  private waveContainer?: HTMLElement | null;
  /**
   * @deprecated 後方互換のため残置。`visibleEndSec` と同値で更新される。
   */
  private maxDisplayDurationSec?: number;
  /**
   * 描画/操作レンジの右端（秒）。
   * seek/play クランプ・hover などUIの上限として参照される。
   * `undefined` のときは WaveSurfer の `getDuration()` をそのまま上限とする。
   */
  private visibleEndSec?: number;
  private lastAudioUrl?: string | null;
  // VS は生PCMから算出したピーク包絡を URL 単位でキャッシュし、同一URLの再init時に
  // 再ダウンロードを避ける（ピークは数KBなので保持コストは小さい）。
  private cachedPeaks?: WavPeaksResult;
  private cachedPeaksUrl?: string | null;
  private instanceSeq = 0;
  private abortController?: AbortController;
  private readonly ngZone = inject(NgZone);

  // 検出イベントの区間再生（playRange）で登録したリスナの解除関数と安全弁タイマー。
  // ユーザー操作（togglePlay/seek）や破棄時に解除して、意図しない pause を防ぐ。
  private rangeStopUnsubs: Array<() => void> = [];
  private rangeStopTimer?: ReturnType<typeof setTimeout>;

  // ブラウザ内 WAV キャッシュ（blob: URL）。初期化時に取得した本体バイトを Blob として
  // 保持し、再生は SAS ではなくこのローカル URL から行う。これにより放置で SAS が
  // 期限切れになっても再生時 403 にならない。サイズ上限を超える長尺は従来の SAS
  // ストリーミングのままにする（メモリ保護）。
  private cachedBlobUrl?: string;
  private cachedBlobUrlSource?: string | null;
  // blob 化する上限（実測受信バイトで判定）。VS など数分の WAV は数MBで十分収まる。
  private readonly maxBlobCacheBytes = 25 * 1024 * 1024;

  initialize(options: WavePlayerInitOptions): boolean {
    const container = options.container;
    this.waveContainer = container;
    const audioUrl = options.audioUrl ?? options.recordData?.data?.storage?.audio;

    if (!container || !audioUrl) {
      console.warn('[WARN] WaveService.initialize skipped due to missing container/audio', {
        hasContainer: !!container,
        hasAudioUrl: !!audioUrl,
        recordId: options.recordData?.id,
      });
      return false;
    }
    const containerWidth =
      container.clientWidth ||
      container.offsetWidth ||
      container.getBoundingClientRect?.().width ||
      0;
    if (!container.isConnected || containerWidth <= 0) {
      console.warn('[WARN] WaveService.initialize skipped due to non-renderable container', {
        isConnected: container.isConnected,
        width: containerWidth,
        recordId: options.recordData?.id,
      });
      return false;
    }

    const sameAudioSource = this.lastAudioUrl === audioUrl;
    this.destroyInternal({ preserveSourceCache: sameAudioSource });
    if (!sameAudioSource) {
      // URL が変わったら算出済みピークは無効化（同一URLの再initでは保持して再DLを避ける）。
      this.cachedPeaks = undefined;
      this.cachedPeaksUrl = undefined;
    }
    this.lastAudioUrl = audioUrl;
    this.isReady = false;
    // 後で再生成できるように初期化オプションを保持する（shallow copyで十分）
    this.lastInitOptions = { ...options };
    this.setMaxDurationsFromVideo(options.videoDurationSec ?? undefined);
    const instanceId = ++this.instanceSeq;

    // v7: プラグインは create を呼び出してインスタンスを得て、plugins配列に渡す
    const hoverPlugin = HoverPlugin.create({
      labelBackground: 'rgba(0, 0, 0, 0.7)',
      labelColor: '#fff',
      labelSize: 12,
      labelPreferLeft: false,
      lineColor: options.colors.cursorColor ?? '#CCCCCC',
      lineWidth: 2,
      formatTimeCallback: (seconds) => this.formatTimestamp(seconds),
    });

    // 初期ズームの当たり（ready 時に実 duration で resetZoom 再計算されるため概算で十分）。
    // wavDuration メタデータがあれば使い、無ければ既定値にフォールバックする。
    const minPx = this.computeInitialPxPerSec(
      container,
      options.wavDurationMs ?? null,
    );
    const targetPeaks = this.computeTargetPeaks(
      options.wavDurationMs ?? null,
      options.videoDurationSec ?? null,
    );
    // デバッグ用ログ
    this.initialMinPxPerSec = minPx;

    const timelinePlugin = TimelinePlugin.create({ height: 15 });
    const regionsPlugin = RegionsPlugin.create();
    const zoomPlugin = ZoomPlugin.create({
      maxZoom: 400,
      scale: minPx,
    });
    const isRSST = options.recordData?.data?.recordType === 'RSST';

    const waveSurferOptions: WaveSurferOptions = {
      container,
      // 全音声プロトコルで、生PCMから算出したピークを渡し <audio> で逐次再生する MediaElement。
      // 全量デコードを避けるためバックエンドは常に MediaElement（波形はピーク包絡で描画）。
      backend: 'MediaElement',
      height: isRSST ? 220 : 90,
      waveColor: options.colors.waveColor ?? '#8a97a5',
      progressColor: options.colors.progressColor ?? '#008acb',
      cursorColor: options.colors.cursorColor ?? '#CCCCCC',
      cursorWidth: 1,
      barWidth: undefined,
      interact: true,
      dragToSeek: true,
      autoScroll: true,
      autoCenter: true,
      minPxPerSec: minPx,
      normalize: true,
      plugins: [
        timelinePlugin,
        regionsPlugin,
        hoverPlugin,
        zoomPlugin,
      ],
    };

    const wsOptions = waveSurferOptions;

    this.wavesurfer = WaveSurfer.create(wsOptions);
    // 再生速度は必ず1.0でリセット（前回の設定が残って意図しない速度になるのを防ぐ）
    try {
      this.wavesurfer.setPlaybackRate(1);
    } catch {
      // ignore
    }
    // <audio> には SAS URL を渡すが、波形描画は別途算出するピークで行う。preload を metadata に
    // 絞り、本体WAVのバックグラウンド先読み（VSでは DRM 動画ストリームとの帯域競合）を防ぐ。
    // 再生時はブラウザが必要範囲を逐次取得する。
    try {
      const mediaEl = (this.wavesurfer as any).getMediaElement?.();
      if (mediaEl) {
        mediaEl.preload = 'metadata';
      }
    } catch {
      // ignore
    }
    this.applyColorOptions({
      waveColor: options.colors.waveColor ?? '#8a97a5',
      progressColor: options.colors.progressColor ?? '#008acb',
      cursorColor: options.colors.cursorColor ?? '#CCCCCC',
    });

    this.regionsPlugin = regionsPlugin;
    this.hoverPlugin = hoverPlugin;

    const cb = options.callbacks;
    this.wavesurfer.on('ready', () => {
      // 全プロトコルが非同期にピークを算出するため、stale インスタンスのコールバックは破棄する。
      if (instanceId !== this.instanceSeq) return;
      this.isReady = true;
      // ready 時点の描画情報を記録
      this.logWaveState('ready');
      cb.onProcessingDone?.();
      if (!options.suppressReadyCallback) {
        cb.onReady();
      }
    });
    this.wavesurfer.on('play', () => { if (instanceId !== this.instanceSeq) return; cb.onPlay(); });
    this.wavesurfer.on('pause', () => { if (instanceId !== this.instanceSeq) return; cb.onPause(); });
    this.wavesurfer.on('finish', () => { if (instanceId !== this.instanceSeq) return; cb.onFinish(); });
    this.wavesurfer.on('audioprocess', () => { if (instanceId !== this.instanceSeq) return; cb.onAudioProcess(); });
    this.wavesurfer.on('seeking', (time: number) => {
      if (instanceId !== this.instanceSeq) return;
      const clamped = this.clampToMaxDuration(time);
      if (this.wavesurfer && clamped !== time) {
        const duration = this.wavesurfer.getDuration?.() ?? 0;
        if (duration > 0) {
          this.wavesurfer.seekTo(clamped / duration);
        }
      }
      cb.onSeeking(clamped);
    });
    this.wavesurfer.on('error', (e: any) => {
      if (instanceId !== this.instanceSeq) return;
      // AbortError は意図的なキャンセル（レコード切替等）なので無視
      if (e?.name === 'AbortError' || (typeof e === 'string' && e.includes('AbortError'))) return;
      cb.onProcessingDone?.();
      cb.onDownloadDone?.();
      cb.onError(e);
    });
    if (cb.onLoading) {
      this.wavesurfer.on('loading', (p: number) => {
        if (instanceId !== this.instanceSeq) return;
        if (p >= 100) return;
        cb.onLoading!(p);
      });
    }

    // 全音声プロトコルで生PCMをストリーム取得しながらピーク包絡を算出する。
    // AbortController で fetch をキャンセル可能にする
    this.abortController?.abort();
    this.abortController = new AbortController();
    const abortSignal = this.abortController.signal;

    const onLoadError = (err: unknown) => {
      if ((err as { name?: string } | null)?.name === 'AbortError') return;
      cb.onError(err);
    };

    const isStale = () => instanceId !== this.instanceSeq || abortSignal.aborted;

    const loadAudio = async () => {
      // 同一URLの算出済みピークがあれば再ダウンロードしない。blob キャッシュがあれば
      // 再生も SAS ではなくローカル blob から行う（期限切れに左右されない）。
      if (this.cachedPeaksUrl === audioUrl && this.cachedPeaks) {
        const cached = this.cachedPeaks;
        const reuseUrl =
          this.cachedBlobUrl && this.cachedBlobUrlSource === audioUrl
            ? this.cachedBlobUrl
            : audioUrl;
        this.wavesurfer
          ?.load(reuseUrl, [cached.peaks], cached.durationSec)
          ?.catch?.(onLoadError);
        return;
      }

      try {
        cb.onDownloadStart?.();
        const resp = await fetch(audioUrl, { cache: 'no-store', signal: abortSignal });
        if (!resp.ok) {
          const statusMsg = resp.status === 403
            ? '音声データのアクセス権が期限切れです。ページを再読み込みしてください。'
            : `音声データの取得に失敗しました (HTTP ${resp.status})`;
          throw new Error(statusMsg);
        }
        const total = Number(resp.headers.get('content-length') ?? 0);

        // content-length が判明し上限内なら、本体を全取得して blob 化し再生をローカル化する。
        // content-length は gzip 等で実バイトと食い違いうるため、収集中も実測バイトで上限を再判定。
        const tryBlobCache = total > 0 && total <= this.maxBlobCacheBytes && !!resp.body;
        if (tryBlobCache) {
          await this.loadViaBlobCache(resp, audioUrl, total, targetPeaks, {
            isStale,
            onProgress: (p) => cb.onDownloadProgress?.(p),
            onDone: () => cb.onDownloadDone?.(),
            onLoadError,
          });
          return;
        }

        const reader = resp.body?.getReader();
        // 生PCMをストリーム取得しながら一定メモリでピーク包絡を算出（全量decode/再エンコード不要）。
        // duration は WAV ヘッダ実値を使うのでバイトレート仮定・wavDuration に依存しない。
        const result = reader
          ? await streamWavPeaks(reader, {
              contentLength: total > 0 ? total : undefined,
              targetPeaks,
              signal: abortSignal,
              onProgress: (p) => cb.onDownloadProgress?.(p),
            })
          : computeWavPeaksFromBuffer(new Uint8Array(await resp.arrayBuffer()), targetPeaks);
        cb.onDownloadDone?.();
        if (isStale()) return;

        this.cachedPeaks = result;
        this.cachedPeaksUrl = audioUrl;
        // 大尺/サイズ不明は従来どおり SAS URL を逐次再生（メモリ保護のため blob 化しない）。
        // load() の Promise を catch しないと destroy() 時の abort が unhandled rejection になる。
        this.wavesurfer
          ?.load(audioUrl, [result.peaks], result.durationSec)
          ?.catch?.(onLoadError);
      } catch (err: unknown) {
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
        cb.onDownloadDone?.();
        // WAVパース不能/想定外フォーマット時の安全側フォールバック:
        // 軽量フラット peaks + 直接URL（duration省略=ヘッダ実値）で逐次再生。波形形状は出ないが再生可。
        console.warn('[WaveDebug] peak extraction failed, fallback to flat streaming', err);
        const peaks = this.createPlaceholderPeaks(60);
        this.wavesurfer?.load(audioUrl, peaks)?.catch?.(onLoadError);
      }
    };
    loadAudio().catch((err: any) => {
      // AbortError は無視
      if (err?.name === 'AbortError') return;
      console.warn('[WaveDebug] loadAudio outer failed', err);
      cb.onProcessingDone?.();
      cb.onDownloadDone?.();
      cb.onError(err);
    });

    options.regionService.init(this.regionsPlugin, options.events);
    options.waveTimeline.reset();
    options.waveTimeline.init({
      timelineOffsetReference: options.timelineOffsetReference,
      hiddenRegion: null,
      hiddenRegionParams: options.hiddenRegionParams,
      waveSurferRegions: this.regionsPlugin,
      currentStartOffset: options.currentStartOffset,
      rsstStartMarker: null,
      wavesurfer: this.wavesurfer,
      timelinePlugin,
    });
    options.waveTimeline.refreshTimelineOffset();
    this.timelineService = options.waveTimeline;

    return true;
  }

  /**
   * 本体WAVを全取得して blob: URL を作り、再生をローカル化する（SAS非依存）。
   * 進捗付きで全チャンクを収集し、実測バイトで上限を再判定する。peak 算出に失敗しても
   * フラット peaks で blob 再生を維持し、SAS には戻さない。
   */
  private async loadViaBlobCache(
    resp: Response,
    audioUrl: string,
    total: number,
    targetPeaks: number,
    hooks: {
      isStale: () => boolean;
      onProgress: (p: number | null) => void;
      onDone: () => void;
      onLoadError: (err: unknown) => void;
    },
  ): Promise<void> {
    const reader = resp.body!.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    for (;;) {
      if (hooks.isStale()) {
        try { await reader.cancel(); } catch { /* ignore */ }
        return;
      }
      const { done, value } = await reader.read();
      if (done) break;
      if (!value || value.length === 0) continue;
      received += value.length;
      if (received > this.maxBlobCacheBytes) {
        // 実測がしきい値超過（content-length 誤申告/gzip 等）→ blob 化中止して安全側エラー。
        try { await reader.cancel(); } catch { /* ignore */ }
        throw new Error('WAV exceeds blob cache limit');
      }
      chunks.push(value);
      hooks.onProgress(Math.min(100, Math.round((received / total) * 100)));
    }
    hooks.onDone();
    if (hooks.isStale()) return;

    const buf = this.concatChunks(chunks, received);

    // peaks 算出は失敗してもフラットで継続（blob 再生で SAS 非依存を維持）。
    let peaksArg: (Float32Array | number[])[];
    let durationArg: number | undefined;
    let peaksToCache: WavPeaksResult | null = null;
    try {
      const r = computeWavPeaksFromBuffer(buf, targetPeaks);
      peaksToCache = r;
      peaksArg = [r.peaks];
      durationArg = r.durationSec;
    } catch (peakErr) {
      console.warn('[WaveDebug] peak compute failed on blob, using flat peaks', peakErr);
      peaksArg = this.createPlaceholderPeaks(60);
      durationArg = undefined;
    }

    // blob を「採用」する直前に世代/abort を再確認してから旧URLを revoke → 新規作成する。
    if (hooks.isStale()) return;
    this.revokeCachedBlobUrl();
    const blobUrl = URL.createObjectURL(
      new Blob([buf.buffer as ArrayBuffer], { type: 'audio/wav' }),
    );
    this.cachedBlobUrl = blobUrl;
    this.cachedBlobUrlSource = audioUrl;
    if (peaksToCache) {
      this.cachedPeaks = peaksToCache;
      this.cachedPeaksUrl = audioUrl;
      this.wavesurfer
        ?.load(blobUrl, peaksArg, durationArg)
        ?.catch?.(hooks.onLoadError);
    } else {
      // peaks 算出失敗時はフラット peaks + blob（duration 省略=ヘッダ実値）で再生。
      this.wavesurfer
        ?.load(blobUrl, peaksArg)
        ?.catch?.(hooks.onLoadError);
    }
  }

  private concatChunks(chunks: Uint8Array[], total: number): Uint8Array {
    const out = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
    return out;
  }

  /** blob: URL を解放する。今使用中の URL を誤って消さないよう state は必ず null 化する。 */
  private revokeCachedBlobUrl(): void {
    if (this.cachedBlobUrl) {
      try { URL.revokeObjectURL(this.cachedBlobUrl); } catch { /* ignore */ }
    }
    this.cachedBlobUrl = undefined;
    this.cachedBlobUrlSource = undefined;
  }

  destroy(): void {
    this.destroyInternal();
  }

  private destroyInternal(
    options: { preserveSourceCache?: boolean } = {},
  ): void {
    const { preserveSourceCache = false } = options;
    this.clearRangeStop();
    const ws = this.wavesurfer;
    const ac = this.abortController;
    this.wavesurfer = undefined;
    this.abortController = undefined;

    // wavesurfer.destroy() は内部 fetch を abort し、その promise rejection が
    // Zone.js に伝播して Angular ErrorHandler に到達する。
    // Zone の外で実行することで unhandled rejection を抑制する。
    try { ac?.abort(); } catch { /* ignore */ }
    if (ws) {
      this.ngZone.runOutsideAngular(() => {
        try { ws.destroy(); } catch { /* ignore */ }
      });
    }
    this.regionsPlugin = undefined;
    this.hoverPlugin = undefined;
    this.timelineService = undefined;
    this.isReady = false;
    if (!preserveSourceCache) {
      this.cachedPeaks = undefined;
      this.cachedPeaksUrl = undefined;
      this.lastAudioUrl = undefined;
      // 別音源への切替・コンポーネント破棄では blob を解放する。
      // 同一音源の再init（preserveSourceCache=true）では保持して再DLを避ける。
      this.revokeCachedBlobUrl();
    }
  }

  setPlaybackRate(rate: number): void {
    if (this.wavesurfer) {
      this.wavesurfer.setPlaybackRate(rate);
    }
  }

  /**
   * 初期化時のパラメータでWaveSurferを再生成して、描画状態を完全にリセットする
   */
  resetToInitial(): boolean {
    if (!this.lastInitOptions) {
      return false;
    }
    return this.initialize(this.lastInitOptions);
  }

  /**
   * 現在の状態に合わせたオフセット値やhiddenRegion設定で波形のみ再生成する
   */
  recreateWave(
    currentStartOffset?: number,
    timelineOffsetReference?: number,
    hiddenRegionParams?: any,
  ): boolean {
    if (!this.lastInitOptions) {
      return false;
    }
    const updatedOptions: WavePlayerInitOptions = {
      ...this.lastInitOptions,
      currentStartOffset: currentStartOffset ?? this.lastInitOptions.currentStartOffset,
      timelineOffsetReference: timelineOffsetReference ?? this.lastInitOptions.timelineOffsetReference,
      hiddenRegionParams: hiddenRegionParams ?? this.lastInitOptions.hiddenRegionParams,
    };
    return this.initialize(updatedOptions);
  }

  getWaveSurfer(): WaveSurfer | undefined {
    return this.wavesurfer;
  }

  /**
   * 描画/操作上限のみを更新する（decode-end トリムは触らない）。
   * AvSync 有効時に WaveSurfer 側の生波形は保持したまま、UI クランプだけを
   * 動画長に揃える用途。
   */
  setVisibleEndSec(visibleEndSec: number | null | undefined): void {
    if (visibleEndSec == null || !Number.isFinite(visibleEndSec) || visibleEndSec <= 0) {
      this.visibleEndSec = undefined;
      return;
    }
    this.visibleEndSec = visibleEndSec;
  }

  /**
   * AvSync 有効時に波形先頭の余剰（front trim）を seek の下限として使うための値。
   * `currentStartOffset` (zoom 由来) とは責務が異なるので別管理。
   * `togglePlay` / `seek` / `seekToTime` 内では `max(currentStartOffset, avSyncLowerBoundSec)` を下限に用いる。
   */
  private avSyncLowerBoundSec = 0;

  setAvSyncLowerBound(seconds: number | null | undefined): void {
    if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
      this.avSyncLowerBoundSec = 0;
      return;
    }
    this.avSyncLowerBoundSec = seconds;
  }

  private resolveLowerBound(currentStartOffset: number): number {
    const offset = Number.isFinite(currentStartOffset) ? Math.max(0, currentStartOffset) : 0;
    return Math.max(offset, this.avSyncLowerBoundSec);
  }

  /**
   * 動画長があとから判明した場合に、波形を動画長に合わせて再生成する。
   */
  updateMaxDuration(videoDurationSec: number): void {
    if (!videoDurationSec || videoDurationSec <= 0) {
      return;
    }
    const current = this.maxDisplayDurationSec ?? 0;
    if (Math.abs(current - videoDurationSec) < 0.01) {
      return;
    }
    this.setMaxDurationsFromVideo(videoDurationSec);
    if (this.lastInitOptions) {
      // VS の動画 duration は再生開始後に安定することがある。ここで波形を
      // 再生成すると再生中に WAV の再取得・再デコードが発生するため、
      // 次回の明示的な初期化まで設定だけ更新して持ち越す。
      this.lastInitOptions = { ...this.lastInitOptions, videoDurationSec };
    }
  }

  getRegionsPlugin(): RegionsPlugin | undefined {
    return this.regionsPlugin;
  }

  debugLogState(_label: string): void {
    return;
  }

  togglePlay(currentStartOffset: number, isPlaying: boolean): boolean {
    if (!this.wavesurfer) {
      return isPlaying;
    }
    // ユーザーの明示的な再生/停止は、進行中の区間再生（playRange）の自動停止を解除する。
    this.clearRangeStop();
    const lowerBound = this.resolveLowerBound(currentStartOffset);
    if (lowerBound > 0 && this.wavesurfer.getCurrentTime() < lowerBound) {
      this.wavesurfer.seekTo(lowerBound / this.wavesurfer.getDuration());
    }

    if (isPlaying) {
      this.wavesurfer.pause();
      return false;
    }
    this.playWithRetry();
    return true;
  }

  /**
   * `wavesurfer.play()` を呼び、返値が Promise なら失敗を握りつぶさず扱う。
   * モバイルでは preload='metadata' のためバッファ未充填で play() が一時的に失敗
   * （AbortError / 未準備）することがある。その場合のみ MediaElement の 'canplay' を
   * 一度待って 1 回だけ再試行する。
   * NotAllowedError（ユーザージェスチャー失効）は再試行しても成功しないためログのみ。
   * 初回 unlock は呼び出し側がジェスチャー内で同期的に呼ぶ前提。
   */
  private playWithRetry(): void {
    const ws = this.wavesurfer;
    if (!ws) {
      return;
    }
    const result = this.safePlay(ws);
    if (!result || typeof result.catch !== 'function') {
      return;
    }
    result.catch((err: unknown) => {
      if ((err as { name?: string } | null)?.name === 'NotAllowedError') {
        console.warn('[WARN] wavesurfer.play() blocked (NotAllowedError)', err);
        return;
      }
      // stale チェック: 再試行までの間にインスタンスが入れ替わっていたら何もしない
      if (this.wavesurfer !== ws) {
        return;
      }
      const media = (ws as unknown as { getMediaElement?: () => HTMLMediaElement })
        .getMediaElement?.();
      if (!media) {
        return;
      }
      const onReady = () => {
        media.removeEventListener('canplay', onReady);
        if (this.wavesurfer !== ws) {
          return;
        }
        const retry = this.safePlay(ws);
        retry?.catch?.((retryErr: unknown) => {
          console.warn('[WARN] wavesurfer.play() retry failed', retryErr);
        });
      };
      media.addEventListener('canplay', onReady, { once: true });
    });
  }

  private safePlay(ws: WaveSurfer): Promise<void> | undefined {
    try {
      return ws.play() as unknown as Promise<void> | undefined;
    } catch (err) {
      console.warn('[WARN] wavesurfer.play() threw synchronously', err);
      return undefined;
    }
  }

  /**
   * 検出イベント再生用: `startSec` から一定区間だけ再生して自動停止する。
   * 実時間タイマーではなくメディア時間（getCurrentTime）基準で停止するため、
   * モバイルのバッファ待ちで再生開始が遅れても区間がフルで鳴る。
   * 短い検出イベント（数百ms）でも可聴になるよう最小 1 秒の窓を確保する。
   */
  playRange(startSec: number, durationSec: number, currentStartOffset: number): void {
    const ws = this.wavesurfer;
    if (!ws || !Number.isFinite(startSec)) {
      return;
    }
    const duration = ws.getDuration?.() ?? 0;
    if (!duration || duration <= 0) {
      return;
    }

    // 進行中の区間再生を解除してからシークする。
    this.clearRangeStop();
    this.seekToTime(startSec, currentStartOffset);

    // 停止判定は seek 後の実位置（getCurrentTime）を起点にし、座標系を揃える。
    const actualStart = ws.getCurrentTime?.() ?? startSec;
    // タグの持続時間をそのまま尊重する（タグ幅より長く鳴らさない）。
    // メディア時間基準の停止なので短い窓でも実音声がその長さぶん鳴り、再生前停止は起きない。
    // タグ長が欠損/0 のときのみ既定窓にフォールバックする。
    const FALLBACK_WINDOW_SEC = 1.0;
    const tagged = Number.isFinite(durationSec) ? Math.max(0, durationSec) : 0;
    const window = tagged > 0 ? tagged : FALLBACK_WINDOW_SEC;
    const upperBound = this.visibleEndSec
      ? Math.min(this.visibleEndSec, duration)
      : duration;
    const endSec = Math.min(actualStart + window, upperBound);

    const stopHandler = () => {
      if (this.wavesurfer !== ws) {
        this.clearRangeStop();
        return;
      }
      if ((ws.getCurrentTime?.() ?? 0) >= endSec) {
        this.clearRangeStop();
        try { ws.pause(); } catch { /* ignore */ }
      }
    };
    this.rangeStopUnsubs.push(ws.on('audioprocess', stopHandler));
    this.rangeStopUnsubs.push(ws.on('timeupdate', stopHandler));

    // 安全弁: 再生が始まらない / 停止イベントが来ない場合の絶対上限。
    // 回線遅延で再生開始が遅れても拾えるよう、窓に十分な余裕を加える。
    this.rangeStopTimer = setTimeout(() => {
      this.clearRangeStop();
      if (this.wavesurfer === ws) {
        try { ws.pause(); } catch { /* ignore */ }
      }
    }, (window + 12) * 1000);

    this.playWithRetry();
  }

  private clearRangeStop(): void {
    for (const unsub of this.rangeStopUnsubs) {
      try { unsub(); } catch { /* ignore */ }
    }
    this.rangeStopUnsubs = [];
    if (this.rangeStopTimer) {
      clearTimeout(this.rangeStopTimer);
      this.rangeStopTimer = undefined;
    }
  }

  seek(position: number, currentStartOffset: number): void {
    if (!this.wavesurfer) {
      return;
    }
    // ユーザーの明示的なシークは区間再生の自動停止を解除する。
    this.clearRangeStop();
    if (position === 0) {
      const lowerBound = this.resolveLowerBound(currentStartOffset);
      this.wavesurfer.seekTo(lowerBound / this.wavesurfer.getDuration());
      return;
    }
    if (position === 1) {
      this.wavesurfer.seekTo(1);
      this.wavesurfer.pause();
    }
    const duration = this.wavesurfer.getDuration();
    if (duration > 0 && this.visibleEndSec && position * duration > this.visibleEndSec) {
      const clamped = this.visibleEndSec / duration;
      this.wavesurfer.seekTo(clamped);
      return;
    }
  }

  seekToTime(seconds: number, currentStartOffset: number): void {
    if (!this.wavesurfer || !Number.isFinite(seconds)) {
      return;
    }
    const duration = this.wavesurfer.getDuration();
    if (!duration || duration <= 0) {
      return;
    }
    const lowerBound = this.resolveLowerBound(currentStartOffset);
    const upperBound = this.visibleEndSec
      ? Math.min(this.visibleEndSec, duration)
      : duration;
    const clamped = Math.min(Math.max(seconds, lowerBound), upperBound);
    this.wavesurfer.seekTo(clamped / duration);
  }

  applyZoom(delta: number, currentValue: number): number {
    if (!this.timelineService || !this.isReady || !this.wavesurfer) {
      return currentValue;
    }
    const midTime = this.getVisibleMidTime(this.wavesurfer);
    const next = this.timelineService.applyZoom(this.wavesurfer, currentValue, delta);
    // ボタン操作時はズーム前に見えていた中央付近を維持する
    if (this.wavesurfer && midTime !== null) {
      const duration = this.wavesurfer.getDuration?.() ?? 0;
      if (duration > 0) {
        const clamped = Math.min(Math.max(midTime, 0), duration);
        this.wavesurfer.seekTo(clamped / duration);
      }
    }
    return next;
  }

  resetZoom(currentValue: number, currentStartOffset: number = 0): number {
    if (!this.timelineService) {
      return currentValue;
    }
    return this.timelineService.resetZoom(this.wavesurfer, currentStartOffset);
  }

  /**
   * 波形を初期状態から再描画する。WaveSurferインスタンスを再生成する。
   */
  resetView(currentStartOffset: number = 0): void {
    const ws: any = this.wavesurfer as any;
    if (!ws) {
      return;
    }
    if (!this.isReady) {
      return;
    }

    // 一時的に autoCenter/autoScroll を無効化してドリフトを防ぐ
    ws.setOptions?.({ autoCenter: false, autoScroll: false });

    if (this.initialMinPxPerSec !== undefined) {
      ws.setOptions?.({ minPxPerSec: this.initialMinPxPerSec });
    }

    ws.zoom?.(0);
    this.clampAndCenter(ws, currentStartOffset);
    this.timelineService?.refreshTimelineOffset();
    this.logWaveState('resetView');

    // 元の設定に戻す
    ws.setOptions?.({ autoCenter: true, autoScroll: true });
  }

  refreshTimelineOffset(): void {
    this.timelineService?.refreshTimelineOffset();
  }

  private formatTimestamp(timeInSeconds: number): string {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds - minutes * 60;
    const secondsText = seconds < 10 ? `0${seconds.toFixed(2)}` : seconds.toFixed(2);
    return `${minutes}:${secondsText}`;
  }

  private computeInitialPxPerSec(container: HTMLElement, wavDurationMs: number | null): number {
    if (!wavDurationMs || wavDurationMs <= 0) {
      return 50;
    }
    const durationSec = wavDurationMs / 1000;
    const containerWidth = container.clientWidth || container.offsetWidth || 800;
    const pxPerSec = containerWidth / durationSec;
    const MIN = 5;
    const MAX = 200;
    return Math.min(Math.max(pxPerSec, MIN), MAX);
  }

  private computeTargetPeaks(
    wavDurationMs: number | null | undefined,
    videoDurationSec: number | null | undefined,
  ): number {
    const wavDurationSec =
      wavDurationMs != null && Number.isFinite(wavDurationMs) && wavDurationMs > 0
        ? wavDurationMs / 1000
        : 0;
    const fallbackDurationSec =
      videoDurationSec != null && Number.isFinite(videoDurationSec) && videoDurationSec > 0
        ? videoDurationSec
        : 0;
    const durationSec = wavDurationSec || fallbackDurationSec;
    if (!durationSec) {
      return this.MIN_RENDER_PEAKS;
    }
    const target = Math.ceil(durationSec * this.PEAKS_PER_SECOND_FOR_ZOOM);
    return Math.min(
      Math.max(target, this.MIN_RENDER_PEAKS),
      this.MAX_RENDER_PEAKS,
    );
  }

  private createPlaceholderPeaks(durationSec: number): Float32Array[] {
    const sampleCount = Math.min(
      Math.max(Math.ceil(durationSec * 2), 256),
      8000,
    );
    return [new Float32Array(sampleCount)];
  }

  private applyColorOptions(colors: { waveColor: string; progressColor: string; cursorColor: string }): void {
    if (!this.wavesurfer) {
      return;
    }
    this.wavesurfer.setOptions({
      waveColor: colors.waveColor,
      progressColor: colors.progressColor,
      cursorColor: colors.cursorColor,
      cursorWidth: 2,
    });
  }

  /**
   * 現在位置が 0〜duration の範囲に収まるよう調整し、ビューを左端に寄せる。
   */
  private clampAndCenter(ws: any, startOffset: number): void {
    if (!ws || typeof ws.getDuration !== 'function' || typeof ws.seekTo !== 'function') {
      return;
    }
    const duration = ws.getDuration();
    if (!duration || duration <= 0) {
      return;
    }
    const current = typeof ws.getCurrentTime === 'function' ? ws.getCurrentTime() : 0;
    const clampStart = Math.max(0, startOffset ?? 0);
    const clamped = Math.min(Math.max(current, clampStart), duration);
    const pos = duration > 0 ? clamped / duration : 0;
    ws.seekTo(pos);
  }

  /**
   * 現在表示している波形領域の中央付近の時刻を推定する。
   * wrapper の scrollLeft/scrollWidth が取得できない場合は getCurrentTime を使う。
   */
  private getVisibleMidTime(ws: WaveSurfer | undefined): number | null {
    if (!ws || typeof (ws as any).getWrapper !== 'function' || typeof ws.getDuration !== 'function') {
      return null;
    }
    const duration = ws.getDuration();
    if (!duration || duration <= 0) {
      return null;
    }
    const wrapper = (ws as any).getWrapper?.();
    if (wrapper) {
      const clientW = (wrapper as HTMLElement).clientWidth;
      const scrollW = (wrapper as HTMLElement).scrollWidth;
      const scrollL = (wrapper as HTMLElement).scrollLeft;
      if (clientW > 0 && scrollW > 0) {
        const ratio = (scrollL + clientW / 2) / scrollW;
        return duration * Math.min(Math.max(ratio, 0), 1);
      }
    }
    // fallback
    return typeof ws.getCurrentTime === 'function' ? ws.getCurrentTime() : null;
  }

  private logWaveState(label: string): any {
    try {
      const ws: any = this.wavesurfer as any;
      if (!ws) {
        return null;
      }
      const duration = ws.getDuration ? ws.getDuration() : 'n/a';
      const container = this.lastInitOptions?.container ?? null;
      const wrapperEl = (ws as any).getWrapper?.() ?? container ?? null;
      const canvases = wrapperEl ? wrapperEl.querySelectorAll('canvas').length : 'n/a';
      const shadowCanvases = (container as any)?.shadowRoot
        ? (container as any).shadowRoot.querySelectorAll('canvas').length
        : 'n/a';
      const contWidth = container?.clientWidth;
      const contHeight = container?.clientHeight;
      const rect = container?.getBoundingClientRect();
      let scrollLeft: any = 'n/a';
      try {
        scrollLeft = wrapperEl && 'scrollLeft' in wrapperEl ? (wrapperEl as HTMLElement).scrollLeft : 'n/a';
      } catch {
        scrollLeft = 'err';
      }
      let timelineScroll: any = 'n/a';
      try {
        const timelineEl = this.timelineService ? (this.timelineService as any)['state']?.timelineElement : null;
        timelineScroll = timelineEl && 'scrollLeft' in timelineEl ? (timelineEl as HTMLElement).scrollLeft : 'n/a';
      } catch {
        timelineScroll = 'err';
      }
      // transform / viewBox も取得（v7は translate で動かすことが多い）
      let transform: any = 'n/a';
      let viewBox: any = 'n/a';
      try {
        transform = (wrapperEl as HTMLElement)?.style?.transform ?? 'n/a';
        const svg = wrapperEl ? wrapperEl.querySelector('svg') : null;
        viewBox = svg ? svg.getAttribute('viewBox') : 'n/a';
      } catch {
        transform = 'err';
        viewBox = 'err';
      }
      // ピーク情報（WebAudioの場合のみ）
      let peaksInfo: any = 'n/a';
      try {
        const peaks = (ws.backend as any)?.getPeaks?.(ws.getDuration ? ws.getDuration() : undefined, contWidth ?? undefined);
        peaksInfo = peaks ? `len:${peaks.length}` : 'none';
      } catch {
        peaksInfo = 'err';
      }
      // renderer 情報
      let rendererName: any = 'n/a';
      try {
        rendererName = (ws as any).getRenderer?.()?.constructor?.name ?? 'n/a';
      } catch {
        rendererName = 'err';
      }
      const info = {
        ready: this.isReady,
        duration,
        hasWs: true,
        canvases,
        shadowCanvases,
        contWidth,
        contHeight,
        rect,
        scrollLeft,
        timelineScroll,
        transform,
        viewBox,
        renderer: rendererName,
        peaks: peaksInfo,
      };
      return info;
    } catch {
      // ignore
      return null;
    }
  }

  /**
   * 動画長を超える位置をクランプする
   */
  private clampToMaxDuration(time: number): number {
    if (!this.visibleEndSec || time <= this.visibleEndSec) {
      return time;
    }
    return this.visibleEndSec;
  }

  /**
   * `videoDurationSec` から `visibleEndSec` / `maxDisplayDurationSec` を更新する。
   * AvSync 有効時に `visibleEndSec` のみを別値で上書きしても波形（ピーク）は保持される。
   */
  private setMaxDurationsFromVideo(videoDurationSec: number | undefined): void {
    this.maxDisplayDurationSec = videoDurationSec;
    this.visibleEndSec = videoDurationSec;
  }
}
